import { useField } from "formik";
import { KeyboardEvent } from "react";

import { SxProps, Theme } from "@mui/material/styles";
import TextFieldMui, { TextFieldVariants } from "@mui/material/TextField";

interface ITextFieldProperties {
  className?: string;
  color?: "primary" | "secondary";
  handleBlur?: () => void;
  handleKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
  inputProperties?: Record<string, string>;
  isDisabled?: boolean;
  isFullWidth?: boolean;
  isMultiline?: boolean;
  isRequired?: boolean;
  label?: string;
  name: string;
  rows?: number;
  shouldAutoComplete?: string;
  size?: "medium" | "small";
  sx?: SxProps<Theme>;
  type: string;
  variant?: TextFieldVariants | undefined;
}

/**
 *
 * Leverages Material UI and Formik's `useField` to handle its state.
 *
 * @param name - The key for Formik's useFields.
 * @param label - The label displayed for the TextField.
 * @param type - The type of TextField (e.g., text, password).
 * @param isRequired - If true, the TextField is marked as required.
 * @param isFullWidth - If true, the TextField will take up the full width of its container.
 * @param isMultiline - If true, the TextField will be multiline.
 * @param rows - The number of rows for multiline TextField.
 * @param inputProperties - Additional properties to pass to the TextField input.* @param color - The color of the TextField (e.g., primary, secondary).
 * @param color - The color of the TextField (e.g., primary, secondary).
 * @param isDisabled - If true, the TextField will be isDisabled.
 * @param shouldAutoComplete - Should TextField autocomplete?
 * @param handleBlur - Optional Function to trigger on blurring TextField.
 * @param sx - Pass down sx styling.
 * @param size - The size of TextField.
 * @param handleKeyDown - Optional Function to trigger on key down event.
 * @param className - Additional class name for TextField.
 * @param variant - The variant of TextField (e.g., standard, outlined, filled).
 *
 */
export default function TextField({
  className = undefined,
  color = "secondary",
  handleBlur = () => {
    /* Do Nothing */
  },
  handleKeyDown = () => {
    /* Do Nothing */
  },
  inputProperties = {},
  isDisabled = false,
  isFullWidth = false,
  isMultiline = false,
  isRequired = false,
  label,
  name,
  rows = 1,
  shouldAutoComplete = "on",
  size = "medium",
  sx,
  type,
  variant = "outlined",
}: ITextFieldProperties): JSX.Element {
  const [field, meta, helpers] = useField(name);

  return (
    <TextFieldMui
      autoComplete={shouldAutoComplete}
      className={className}
      color={color}
      disabled={isDisabled}
      error={!!(meta.touched && meta.error)} // if field has been touched and there is an error, set error to true
      fullWidth={isFullWidth}
      helperText={meta.touched && meta.error} // If field has been touched, display the error text as helper text
      inputProps={inputProperties}
      label={label}
      margin="dense"
      multiline={isMultiline}
      name={name}
      onBlur={() => {
        handleBlur();
        void helpers.setTouched(true);
      }} // Custom implementation
      onChange={field.onChange}
      onKeyDown={handleKeyDown}
      required={isRequired}
      rows={rows}
      size={size}
      sx={[...(Array.isArray(sx) ? sx : [sx])]}
      type={type}
      value={field.value}
      variant={variant}
    />
  );
}
